export type BenchmarkSpec = {
  id: string
  label: string
  rate: number
  color: string
  focus: string
  desc: string
  source: string
}

// Text copied verbatim from docs/reference/benchmarks-tooltips.md where applicable
export const BENCHMARKS: BenchmarkSpec[] = [
  {
    id: "sp500",
    label: "S&P 500 Index",
    rate: 7.0,
    color: "#3B82F6", // Blue 500
    focus: "U.S. Large-Cap Equities",
    desc:
      "Tracks the performance of 500 leading U.S. companies across major industries, covering roughly 80 % of total U.S. market capitalization. Maintained by S&P Dow Jones Indices, it is the most widely used benchmark for U.S. equities and serves as a proxy for overall market health.",
    source:
      "Dataset source:\n" +
      "- Provider: S&P Dow Jones Indices\n" +
      "- Index ID: S&P 500 Total Return Index (SPTR)\n" +
      "- Currency: USD → converted to CAD for UI display\n" +
      "- Frequency: Daily (fallback: monthly)\n" +
      "- History: Minimum 10 years total-return series\n" +
      "- Interim proxy: SPY ETF (Adjusted Close, Yahoo Finance)",
  },
  {
    id: "r2000",
    label: "Russell 2000 Index",
    rate: 6.2,
    color: "#A855F7", // Purple 500-ish
    focus: "U.S. Small-Cap Equities",
    desc:
      "Measures performance of roughly 2 000 of the smallest publicly traded U.S. companies within the broader Russell 3000 Index. Created by the Frank Russell Company and now maintained by FTSE Russell (LSEG). Considered the standard small-cap benchmark for U.S. equity managers and a useful diversification proxy.",
    source:
      "Dataset source:\n" +
      "- Provider: FTSE Russell / London Stock Exchange Group\n" +
      "- Index ID: Russell 2000 Total Return (RU20INTR)\n" +
      "- Currency: USD → CAD converted\n" +
      "- Frequency: Daily (fallback monthly)\n" +
      "- History: ≥ 10 years\n" +
      "- Interim proxy: IWM ETF (Adjusted Close, Yahoo Finance)",
  },
  {
    id: "tsx",
    label: "S&P/TSX Composite Index",
    rate: 6.0,
    color: "#10B981", // Emerald 500
    focus: "Canadian Broad Equities",
    desc:
      "The standard measure of Canadian public-market performance, representing approximately 70–80 % of the total market capitalization of all companies listed on the Toronto Stock Exchange. Maintained jointly by S&P Dow Jones Indices and the TMX Group.",
    source:
      "Dataset source:\n" +
      "- Provider: S&P Dow Jones Indices / TMX Group\n" +
      "- Index ID: S&P/TSX Composite Total Return (CAD)\n" +
      "- Currency: CAD (native)\n" +
      "- Frequency: Daily (fallback monthly)\n" +
      "- History: ≥ 10 years\n" +
      "- Interim proxy: XIC.TO ETF (Adjusted Close, Yahoo Finance)",
  },
  {
    id: "tsx_venture",
    label: "S&P/TSX Venture Composite",
    rate: 4.5,
    color: "#F59E0B", // Amber 500
    focus: "Canadian Small / Venture Equities",
    desc:
      "Tracks the performance of early-stage and resource-oriented small-cap companies listed on the TSX Venture Exchange. It captures the higher-risk, higher-volatility segment of Canada’s public equity market. Maintained by S&P Dow Jones Indices / TMX Group.",
    source:
      "Dataset source:\n" +
      "- Provider: S&P Dow Jones Indices / TMX Group\n" +
      "- Index ID: S&P/TSX Venture Composite (Index TR if available)\n" +
      "- Currency: CAD (native)\n" +
      "- Frequency: Daily (fallback monthly)\n" +
      "- History: ≥ 10 years (where available; flag partial coverage)\n" +
      "- Interim proxy: IWC ETF (US micro-cap approximation) or HXV.TO if available\n" +
      "- Notes: If total-return unavailable, mark “Price only” in tooltip metadata.",
  },
  {
    id: "ca_bond",
    label: "FTSE Canada Universe Bond",
    rate: 3.0,
    color: "#64748B", // Slate 500
    focus: "Canadian Investment-Grade Bonds",
    desc:
      "Represents the broad Canadian bond market including federal, provincial, municipal, and investment-grade corporate issuers with maturities > 1 year. Maintained by FTSE Russell (LSEG) and widely used as the institutional benchmark for fixed income portfolios in Canada.",
    source:
      "Dataset source:\n" +
      "- Provider: FTSE Russell / London Stock Exchange Group\n" +
      "- Index ID: FTSE Canada Universe Bond Index (Total Return)\n" +
      "- Currency: CAD (native)\n" +
      "- Frequency: Daily or End-of-Month\n" +
      "- History: ≥ 10 years\n" +
      "- Interim proxy: ZAG.TO ETF (BMO Aggregate Bond Index ETF)",
  },
  {
    id: "balanced_ca",
    label: "Balanced Composite (60/40)",
    rate: 5.0,
    color: "#FBBF24", // Amber/Gold 400
    focus: "Mixed Portfolio (60 % Equities / 40 % Bonds)",
    desc:
      "A model balanced portfolio representing a traditional “60 / 40” asset allocation—60 % equities and 40 % bonds—used to illustrate diversified portfolio performance over time. The index is synthetically constructed for this platform.",
    source:
      "Dataset source:\n" +
      "- Provider: Derived (synthetic)\n" +
      "- Composition:\n  - 60 % MSCI Canada Index (Gross Return, CAD)\n  - 40 % FTSE Canada Universe Bond Index (Total Return, CAD)\n" +
      "- Rebalance: Monthly\n" +
      "- Currency: CAD\n" +
      "- Frequency: Monthly composite series\n" +
      "- History: ≥ 10 years\n" +
      "- Interim proxy: 60 % XIC.TO + 40 % ZAG.TO (ETF Adjusted Close series)",
  },
]

export const MAX_ACTIVE_SERIES = 3
